import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowDown, Sparkles, Download, Check, Loader2,
  Bot, ShieldAlert, FileCheck, ScrollText, Landmark, IdCard, FileText,
  Bell, Mail, ShoppingCart, Building2, BadgeCheck, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Scroll-reveal hook ─────────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}

const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${
        seen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
      style={{ transitionDelay: seen ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
};

/* ─── Content ───────────────────────────────────────────────────── */
// Ordered by "revolutionary → table-stakes". Showstoppers lead.
const USE_CASES = [
  {
    icon: Building2,
    mock: 'budget',
    title: 'Smart Budget Identification & live PO consumption',
    tagline: 'Every budget head knows, in real time, how much has been committed against it',
    body:
      'The moment a PO is raised, the platform identifies the correct budget head and deducts the committed value against it. Every budget head shows a live consumption bar. Total Budgeted, Consumed, Remaining and — critically — At Risk / Exceeded heads surface as first-class KPIs. Finance never has to reconcile spreadsheets to know if a division is about to overrun; the overrun surfaces at PO-creation time, not at month-end.',
    stack: 'AI-assisted budget-head suggestion at PO creation + real-time aggregation across POs, invoices and approvals.',
  },
  {
    icon: Mail,
    mock: 'email',
    title: 'AI Email Hub — inbox → action items in real time',
    tagline: 'Every incoming email is auto-classified into open issues, tagged by priority, and routed for action',
    body:
      'The Email Hub connects the accounts inbox, and an AI layer reads every thread — extracting concrete open issues with priority tags, follow-up / payment / approval-needed classification, and assignment routing. AI-drafted responses maintain a consistent tone across every division and vendor communication. What used to be a mountain of unread threads is now a tracked, statused issue queue.',
    stack: 'Gmail sync + LLM extraction pipeline (Claude Sonnet 4.5) → structured open-issues feed with priority scoring.',
  },
  {
    icon: ScrollText,
    mock: 'wizard',
    title: 'Grant Claim Wizard — 5-step AI gauntlet',
    tagline: 'Every infrastructure grant a division claims is AI-screened before a human sees it',
    body:
      'The wizard evaluates description specificity, justification quality per item, vendor quotation authenticity (letterhead, signature, amount ±5%, GSTIN), letterhead-request completeness and committee resolution presence. Only claims that clear all five reach the reviewer. Coaching suggestions surface inline — the AI tells the secretary how to fix a weak justification, not just that it\'s weak.',
    stack: 'Gemini 3 Flash (multimodal PDF) + Claude Sonnet (text eval) via Emergent Universal Key.',
  },
  {
    icon: BadgeCheck,
    mock: 'approvals',
    title: 'Smart Approvals — pre-configured routing for budgets & payments',
    tagline: 'Every payment approval follows the tournament\u2019s configured hierarchy — no ad-hoc WhatsApp sign-offs',
    body:
      'Payment approvals are structured: Draft → Pending → Approved with total-amount rollups, per-tournament scoping, and per-invoice traceability. Pre-configured routing rules identify the correct approver for each budget head and threshold, and the AI drafts the approval request email with the invoice, PO, and budget-head context already inline. Reviewers see the exact numbers they need to sign off on — nothing more, nothing less.',
    stack: 'Rule engine + AI-drafted approval requests over the ledger context.',
  },
  {
    icon: FileCheck,
    shot: 'invoices.png',
    title: 'Invoice-to-PO matching',
    tagline: 'Every vendor invoice auto-matched against its originating Purchase Order',
    body:
      'A vendor emails a PDF invoice. The platform extracts vendor, GSTIN, amount, line items — then matches against the PO the finance team raised weeks earlier. Amount mismatches, scope drift, over-billing all flagged instantly. No re-typing into Excel. No cross-checking email threads.',
    stack: 'Multi-modal LLM (PDF → structured extract) + rule-based reconciliation.',
  },
  {
    icon: IdCard,
    shot: 'vendors.png',
    title: 'Vendor KYC extraction',
    tagline: 'Onboard a vendor by dropping their letterhead — PAN, GSTIN, MSME status auto-filled',
    body:
      "A new vendor's registration certificate PDF is enough. The platform reads it, extracts PAN, GSTIN, address, MSME status, TDS-applicable section — and populates the vendor master. Deduplication check runs on PAN/GSTIN so the same vendor never appears twice under two different names.",
    stack: 'Multimodal LLM PDF-to-structured extraction with schema validation.',
  },
  {
    icon: ShieldAlert,
    shot: 'budget_upload.png',
    title: 'Budget-upload anomaly detection',
    tagline: 'AI flags every numerical outlier at the moment of upload',
    body:
      "MPCA's tournament budget is uploaded once per season. The AI layer inspects every line — negative amounts, duplicate cost codes, currency mismatches, order-of-magnitude outliers against last year's baseline. What used to be caught in the March audit is now caught before the season begins.",
    stack: 'LLM prompt over parsed CSV with row-level structured output.',
  },
  {
    icon: Landmark,
    shot: 'bank.png',
    title: 'Bank-reconciliation auto-match',
    tagline: 'Statement lines meet system entries — fuzzy-matched by amount + date + narration',
    body:
      'Upload the bank statement; the AI matches each line to the corresponding invoice payment or grant disbursement, tolerating narration typos, split payments, and out-of-order clearances. Unmatched lines rise to the top for human eyes. Month-end reconciliation shrinks from a two-day exercise to a coffee break.',
    stack: 'LLM-guided fuzzy matching + amount/date proximity scoring.',
  },
  {
    icon: FileText,
    shot: 'reports.png',
    title: 'Natural-language executive reports',
    tagline: '"Show me sponsor collections vs commitments by month, MPL 2026" → PDF in 10 seconds',
    body:
      'Board reports, sponsor summaries, tournament-window cashflows — all generated by prompt. The AI queries the ledger, formats the answer as an executive report with charts and commentary, exports to PDF. No BI-tool learning curve. No analyst dependency for one-off questions.',
    stack: 'LLM report-builder chain with ReportLab output.',
  },
  {
    icon: Bot,
    shot: 'ai_assistant.png',
    title: 'AI Assistant across every module',
    tagline: 'Natural-language Q&A over the entire finance ledger — no SQL, no BI licences',
    body:
      'Ask "which invoices are overdue > 15 days for the broadcast vendors?" and get a sourced answer in seconds. The assistant queries the live database, cites the underlying records, and drafts follow-up emails on demand. A 20-minute spreadsheet exercise becomes a 20-second question.',
    stack: 'Claude Sonnet 4.5 via Emergent Universal LLM Key + custom RAG over MongoDB.',
  },
];

const TAIL = [
  { icon: Bell, label: 'Alerts', blurb: 'LLM-composed alert copy with anomaly-detection rules — budget overruns, stalled approvals, unusual vendor spend surface in-app + email.' },
  { icon: ShoppingCart, label: 'PO auto-classification', blurb: 'Every purchase order gets an AI-suggested budget-head + vendor-history context inline at the moment of approval.' },
  { icon: FileCheck, label: 'Accounting entries', blurb: 'AI-drafted journal entries for tournament-day cash movements — categorised, tagged and ready for the ledger.' },
];

const METRICS = [
  { task: 'Grant claim: submit → payment initiated', conv: '9–14 days', mpl: '< 60 minutes', save: '~99%' },
  { task: 'Invoice-to-PO reconciliation (per invoice)', conv: '12 minutes', mpl: '< 30 seconds', save: '~96%' },
  { task: 'Bank reconciliation (monthly)', conv: '1.5–2 days', mpl: '< 45 minutes', save: '~96%' },
  { task: 'Vendor onboarding (KYC entry)', conv: '20 minutes', mpl: '2 minutes', save: '~90%' },
  { task: 'Budget-head identification for a new PO', conv: '5–8 minutes', mpl: 'Auto-suggested', save: '~95%' },
  { task: 'Inbox → actionable task list', conv: 'Manual triage', mpl: 'Live feed', save: '~90%' },
  { task: 'Payment approval drafting + routing', conv: '15–20 minutes', mpl: '< 1 minute', save: '~95%' },
  { task: 'Ad-hoc executive report', conv: '45 min (analyst)', mpl: '10 seconds', save: '~99%' },
  { task: 'Budget-anomaly detection', conv: 'Month-end audit', mpl: 'At upload', save: 'Prevents' },
];

const ARCH = [
  ['User interface', 'React 19 + Tailwind + shadcn/ui', 'Portal (division secretaries) & Console (MPCA finance team)'],
  ['API + workflow', 'FastAPI + async Python', 'Auth, business logic, PDF generation, email dispatch'],
  ['AI orchestration', 'Emergent Universal LLM Key', 'Single credential fanning out to Claude, Gemini, GPT-Image'],
  ['Data', 'MongoDB', 'Claims, vendors, invoices, POs, ledger, audit history'],
  ['Notifications', 'Resend', 'Every status change, submission, and reviewer action'],
  ['Storage', 'Object storage', 'Quotations, letterheads, resolutions, invoices'],
];

/* ─── Page ───────────────────────────────────────────────────────── */
export default function CaseStudy() {
  useEffect(() => {
    document.title = 'MPL 2026 · Finance Console Case Study — MPCA × Delhigence';
  }, []);

  const scrollToForm = () => {
    document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToStory = () => {
    document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 selection:bg-emerald-500/40 selection:text-white">
      {/* Ambient background grain */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.035] mix-blend-screen z-0"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* ─── Sticky top bar ─────────────────────────────────────── */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-slate-950/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/brand/mpl-logo.png"
              alt="Madhya Pradesh League"
              className="h-9 w-9 object-contain drop-shadow-[0_0_12px_rgba(249,115,22,0.35)]"
              data-testid="header-mpl-logo"
            />
            <div className="leading-tight">
              <div className="text-[13px] font-semibold tracking-tight text-white">MPL Finance Console</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-orange-400/85">Case Study</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${API}/api/public/case-study-pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-download-pdf-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white transition px-3 py-1.5 rounded-full border border-white/10 hover:border-white/25"
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
            <button
              onClick={scrollToForm}
              data-testid="header-request-demo-btn"
              className="text-xs font-semibold text-white bg-orange-500 hover:bg-orange-400 transition px-3.5 py-1.5 rounded-full shadow-lg shadow-orange-500/25"
            >
              Request Demo
            </button>
          </div>
        </div>
      </header>

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/3 -left-40 w-[700px] h-[700px] rounded-full bg-emerald-600/20 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[650px] h-[650px] rounded-full bg-orange-600/15 blur-[130px]" />
          <div className="absolute top-0 right-1/3 w-[400px] h-[400px] rounded-full bg-blue-900/25 blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-32">
          <Reveal>
            <div className="flex items-center gap-5 mb-10">
              <img
                src="/brand/mpl-logo.png"
                alt="Madhya Pradesh League — Scindia Cup"
                className="h-24 w-24 sm:h-28 sm:w-28 object-contain drop-shadow-[0_0_40px_rgba(249,115,22,0.35)]"
                data-testid="hero-mpl-logo"
              />
              <div>
                <div className="inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.18em] uppercase text-orange-300/90 border border-orange-400/30 rounded-full px-3 py-1 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                  MPCA &nbsp;×&nbsp; Delhigence · A live case study
                </div>
                <div className="text-sm text-slate-400 uppercase tracking-[0.2em]">
                  Madhya Pradesh League · Scindia Cup
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1
              data-testid="hero-title"
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] mb-6"
            >
              How <span className="italic text-orange-300">MPL 2026</span> runs<br className="hidden sm:block" /> on AI.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl leading-relaxed mb-4">
              A behind-the-scenes look at how the
              <span className="text-white font-medium"> Madhya Pradesh Cricket Association</span> replaced
              spreadsheets, email threads and legacy ERPs with a single AI-assisted platform — and why cricket
              associations across India may never look at conventional accounting software the same way again.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                onClick={scrollToStory}
                data-testid="hero-read-btn"
                className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 transition font-semibold text-sm px-5 py-3 rounded-full text-white shadow-lg shadow-orange-500/25"
              >
                Read the story
                <ArrowDown className="w-4 h-4 transition group-hover:translate-y-0.5" />
              </button>
              <button
                onClick={scrollToForm}
                data-testid="hero-demo-btn"
                className="group inline-flex items-center gap-2 border border-emerald-400/40 hover:border-emerald-300 hover:bg-emerald-400/5 transition text-sm px-5 py-3 rounded-full text-emerald-200"
              >
                Request a demo
                <ArrowRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
              </button>
              <a
                href={`${API}/api/public/case-study-pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-pdf-link"
                className="inline-flex items-center gap-2 text-sm px-5 py-3 rounded-full text-slate-300 hover:text-white transition"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            </div>
          </Reveal>

          {/* Stats strip */}
          <Reveal delay={450}>
            <div className="mt-24 grid grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { k: '10', v: 'flagship AI capabilities' },
                { k: '~99%', v: 'faster grant-to-payment cycle' },
                { k: '1', v: 'FTE-quarter recovered per season' },
                { k: '38', v: 'BCCI associations this transfers to' },
              ].map((s, i) => (
                <div
                  key={i}
                  className="border-l-2 border-emerald-400/40 pl-4 py-1"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                    {s.k}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ PART I: The ERP Inversion ═══ */}
      <section id="story" className="relative py-28 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <div className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">Part I</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 tracking-tight">
              The problem was never accounting software.
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-slate-300 text-lg leading-relaxed mb-6">
              Conventional ERPs and accounting software packages are excellent horizontal tools. They&rsquo;ve earned their reputations
              by handling the general accounting needs of millions of Indian businesses. But there&rsquo;s a specific class
              of organisation for whom they have never been quite right — the multi-stakeholder, multi-division,
              tournament-cadence sports body.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-slate-300 text-lg leading-relaxed mb-10">
              A cricket league is not a manufacturing SME. Its finance calendar rises and falls with match days. Its
              grant approvals need vernacular workflows. Its vendor mix — grounds, umpires, broadcasters, hospitality —
              has a shape a general ledger simply cannot see. For years, MPCA — like most cricket associations in India —
              has forced its workflows to bend around the constraints of tools that were never built for the tournament rhythm.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <blockquote className="relative pl-8 py-6 border-l-2 border-emerald-400 my-14">
              <div className="text-2xl sm:text-3xl font-light text-white leading-snug italic tracking-tight">
                &ldquo;The system had to adapt to the accounting software. It never occurred to anyone that the accounting software could be replaced by something built for us.&rdquo;
              </div>
            </blockquote>
          </Reveal>
          <Reveal delay={250}>
            <h3 className="text-xl font-semibold text-emerald-300 mb-4 mt-8">The inversion.</h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              Delhigence&rsquo;s premise was simple: rather than take a horizontal ERP and coax it into cricket-league shape,
              build a vertical stack purpose-designed for the tournament from day zero. Every screen, every workflow,
              every AI capability answered one question — <em className="text-white">does this help MPL 2026 run better?</em>
              The result: a platform that feels lighter than a conventional ERP, moves faster than legacy accounting software, and understands the tournament
              the way its own finance lead does — because it was built by watching that finance lead work.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ PART II: Architecture ═══ */}
      <section className="relative py-28 border-t border-white/5 bg-gradient-to-b from-transparent to-slate-900/50">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal>
            <div className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">Part II</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
              One LLM layer. Seven AI capabilities. Sixteen finance modules.
            </h2>
            <p className="text-slate-400 text-lg mb-12">
              Every module invokes the same Emergent Universal LLM Key — no per-module vendor contract, no rewiring when a better model ships.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="overflow-hidden rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-emerald-950/50 text-emerald-300 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-3.5 text-left font-semibold">Layer</th>
                    <th className="px-5 py-3.5 text-left font-semibold">Technology</th>
                    <th className="px-5 py-3.5 text-left font-semibold">Role</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {ARCH.map(([layer, tech, role], i) => (
                    <tr key={i} className="hover:bg-white/[0.02] transition">
                      <td className="px-5 py-4 font-medium text-white">{layer}</td>
                      <td className="px-5 py-4 text-emerald-300">{tech}</td>
                      <td className="px-5 py-4 text-slate-300">{role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ PART III: 7 Flagship AI Capabilities ═══ */}
      <section className="relative py-28 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">Part III</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
              Ten flagship AI capabilities.
            </h2>
            <p className="text-slate-400 text-lg mb-16">
              Each solves a specific pain of running MPL 2026. Screens show either the live product or a demo-data mock (real client figures are never shared publicly).
            </p>
          </Reveal>

          <div className="space-y-24">
            {USE_CASES.map((uc, i) => {
              const Icon = uc.icon;
              const reverse = i % 2 === 1;
              return (
                <Reveal key={i} delay={50}>
                  <div className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? 'lg:[direction:rtl]' : ''}`}>
                    <div className="lg:col-span-5 lg:[direction:ltr]">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-400/30 grid place-items-center">
                          <Icon className="w-5 h-5 text-emerald-300" />
                        </div>
                        <div className="text-xs font-mono tracking-wider text-slate-500">
                          {String(i + 1).padStart(2, '0')} / {String(USE_CASES.length).padStart(2, '0')}
                        </div>
                      </div>
                      <h3
                        data-testid={`usecase-title-${i}`}
                        className="text-2xl sm:text-3xl font-bold text-white mb-3 tracking-tight leading-tight"
                      >
                        {uc.title}
                      </h3>
                      <p className="text-emerald-300 text-base italic mb-5 leading-relaxed">{uc.tagline}</p>
                      <p className="text-slate-300 leading-relaxed mb-5">{uc.body}</p>
                      <div className="text-xs text-indigo-300 bg-indigo-500/5 border border-indigo-400/20 rounded-lg px-4 py-2.5">
                        <span className="font-semibold text-indigo-200">Under the hood:&nbsp;</span>
                        <span className="text-indigo-200/80">{uc.stack}</span>
                      </div>
                    </div>
                    <div className="lg:col-span-7 lg:[direction:ltr]">
                      {uc.shot ? (
                        <div className="relative group">
                          <div className="absolute -inset-2 bg-gradient-to-tr from-emerald-500/20 to-indigo-500/10 rounded-2xl blur-xl opacity-60 group-hover:opacity-90 transition" />
                          <div className="relative rounded-xl overflow-hidden border border-white/10 bg-slate-900 shadow-2xl">
                            <img
                              src={`${API}/api/public/case-study/shots/${uc.shot}`}
                              alt={uc.title}
                              loading="lazy"
                              className="w-full h-auto block"
                            />
                          </div>
                        </div>
                      ) : uc.mock === 'wizard' ? (
                        <WizardGauntletMock />
                      ) : uc.mock === 'budget' ? (
                        <BudgetMock />
                      ) : uc.mock === 'email' ? (
                        <EmailHubMock />
                      ) : uc.mock === 'approvals' ? (
                        <ApprovalsMock />
                      ) : null}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Tail capabilities */}
          <Reveal delay={100}>
            <div className="mt-28">
              <h3 className="text-xl font-semibold text-white mb-6">Also inside the platform.</h3>
              <div className="grid md:grid-cols-3 gap-5">
                {TAIL.map((t, i) => {
                  const Icon = t.icon;
                  return (
                    <div
                      key={i}
                      className="p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition"
                    >
                      <Icon className="w-5 h-5 text-emerald-300 mb-3" />
                      <div className="text-white font-semibold mb-2">{t.label}</div>
                      <div className="text-sm text-slate-400 leading-relaxed">{t.blurb}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ PART IV: Time Optimisation ═══ */}
      <section className="relative py-28 border-t border-white/5 bg-gradient-to-b from-slate-900/50 to-transparent">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal>
            <div className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">Part IV</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
              The metric that matters most: how much faster does the tournament run?
            </h2>
            <p className="text-slate-400 text-lg mb-12">
              Every capability compresses the finance team&rsquo;s mechanical work — not their judgement. Numbers reflect Delhigence&rsquo;s pilot measurements during the MPCA engagement.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="overflow-hidden rounded-xl border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-emerald-950/50 text-emerald-300 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-5 py-3.5 text-left font-semibold">Workflow / Task</th>
                    <th className="px-5 py-3.5 text-left font-semibold">Conventional ERP</th>
                    <th className="px-5 py-3.5 text-left font-semibold">MPL Console</th>
                    <th className="px-5 py-3.5 text-right font-semibold">Time saved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {METRICS.map((m, i) => (
                    <tr key={i} className="hover:bg-white/[0.02] transition">
                      <td className="px-5 py-4 font-medium text-white">{m.task}</td>
                      <td className="px-5 py-4 text-slate-400">{m.conv}</td>
                      <td className="px-5 py-4 text-emerald-300">{m.mpl}</td>
                      <td className="px-5 py-4 text-right font-bold text-emerald-300 tabular-nums">{m.save}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="mt-12 p-8 rounded-2xl border border-emerald-400/20 bg-emerald-500/[0.03]">
              <div className="flex items-start gap-4">
                <Zap className="w-6 h-6 text-emerald-300 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    What the numbers translate to, in tournament terms.
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    A single MPL 2026 season involves — conservatively — 30 grant claims, 200 vendor invoices,
                    12 monthly bank reconciliations, 60 vendor onboardings and 50+ ad-hoc reports. Across those
                    volumes, the MPCA finance team recovers the equivalent of <span className="text-white font-semibold">one full-time person-quarter</span> of effort per season.
                    That&rsquo;s not headcount to be cut — that&rsquo;s capacity to be reinvested in the parts of the tournament that actually need human attention.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ PART V: Why This Matters ═══ */}
      <section className="relative py-28 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <div className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">Part V</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 tracking-tight">
              A repeatable model for every cricket league in India.
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-slate-300 text-lg leading-relaxed mb-6">
              There are <span className="text-white font-semibold">38 state cricket associations</span> affiliated with BCCI. Every one of them runs a domestic
              tournament with essentially the same operational shape as MPL — multiple divisions, grant claims,
              vendor payments, sponsor collections, and an accounts team fighting the same fight against horizontal ERPs.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-slate-300 text-lg leading-relaxed mb-10">
              The MPL Console is not a bespoke experiment. It&rsquo;s a <span className="text-emerald-300 font-semibold">reference architecture</span> —
              a template built once, refined for MPCA, portable to any league that adopts it. What each new league needs is an
              onboarding sprint to reflect its own division roster, policy caps and reporting cadence. Weeks, not months.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-400/30">
              <div className="text-xs uppercase tracking-widest text-emerald-300 mb-3">The bottom line</div>
              <p className="text-white text-xl leading-relaxed">
                Delhigence has done the hard part. Any BCCI-affiliated league that wants to modernise its finance
                operations can now do so in weeks — with AI at the core, not bolted on the side. The next tournament your
                association runs should not require a heroic accounts department. It should require a well-designed
                platform. <span className="text-emerald-300 font-semibold">That platform now exists.</span>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ Demo Form ═══ */}
      <DemoForm />

      {/* ═══ Footer ═══ */}
      <footer className="relative border-t border-white/5 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/brand/mpl-logo.png" alt="MPL" className="h-10 w-10 object-contain" />
                <div>
                  <div className="text-white font-semibold leading-tight">Madhya Pradesh League</div>
                  <div className="text-[11px] uppercase tracking-widest text-orange-400/80 leading-tight mt-0.5">Scindia Cup · MPL 2026</div>
                </div>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed max-w-md">
                The MPL Finance Console is the finance operations platform behind MPL 2026 — a purpose-built,
                AI-assisted alternative to horizontal ERPs, built in collaboration between MPCA and Delhigence.
              </p>
              <p className="text-xs text-slate-500 leading-relaxed mt-4 max-w-md italic">
                In collaboration with the Madhya Pradesh Cricket Association (MPCA) — whose belief in going first made this reference architecture possible.
              </p>
            </div>
            <div className="md:text-right space-y-3">
              <a
                href={`${API}/api/public/case-study-pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-pdf-link"
                className="inline-flex items-center gap-2 text-sm text-orange-300 hover:text-orange-200 transition border border-orange-400/30 hover:border-orange-300 rounded-full px-5 py-2.5"
              >
                <Download className="w-4 h-4" /> Download the case study PDF
              </a>
              <div className="text-xs text-slate-500">
                © {new Date().getFullYear()} MPCA × Delhigence. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ─── Demo request form ─────────────────────────────────────────── */
function DemoForm() {
  const [form, setForm] = useState({ name: '', organisation: '', email: '', phone: '', message: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    if (!form.name.trim() || !form.organisation.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in name, organisation, email and phone.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/demo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      setDone(true);
      toast.success('Request received. The Delhigence team will be in touch shortly.');
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo-form" className="relative py-28 border-t border-white/5 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent">
      <div className="max-w-3xl mx-auto px-6">
        <Reveal>
          <div className="text-emerald-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3 text-center">
            Get in touch
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold text-white mb-4 tracking-tight text-center">
            See it live.
          </h2>
          <p className="text-slate-400 text-lg text-center max-w-xl mx-auto mb-12">
            Book a 30-minute walkthrough. We&rsquo;ll show you the running platform, share the pilot numbers, and outline what an onboarding sprint looks like for your league.
          </p>
        </Reveal>

        <Reveal delay={100}>
          {done ? (
            <div
              data-testid="demo-form-success"
              className="p-10 rounded-2xl bg-emerald-500/10 border border-emerald-400/40 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400 grid place-items-center mx-auto mb-5">
                <Check className="w-7 h-7 text-emerald-300" strokeWidth={3} />
              </div>
              <div className="text-2xl font-bold text-white mb-2">Request received.</div>
              <p className="text-slate-300 max-w-md mx-auto">
                Thank you, <span className="text-white font-medium">{form.name}</span>. The Delhigence team will reach out to <span className="text-emerald-300">{form.email}</span> within one business day to schedule the walkthrough.
              </p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="demo-form"
              className="p-8 sm:p-10 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <FormField label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="demo-name" required />
                <FormField label="Organisation" value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} testid="demo-org" required />
                <FormField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="demo-email" required />
                <FormField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="demo-phone" required />
              </div>
              {/* Honeypot: hidden from humans, filled by bots. Server drops these silently. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              />
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">
                  What would you like to discuss?
                  <span className="text-slate-600 ml-1 normal-case tracking-normal">(optional)</span>
                </label>
                <textarea
                  data-testid="demo-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="w-full bg-slate-950/50 border border-white/10 focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 outline-none rounded-lg px-4 py-3 text-white placeholder-slate-600 transition"
                  placeholder="Which workflow are you most interested in? Grants, invoices, bank reconciliation, reporting…"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="demo-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-60 disabled:cursor-not-allowed transition text-white font-semibold text-base px-6 py-4 rounded-full shadow-lg shadow-orange-500/25"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Request a demo <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <div className="text-[11px] text-slate-500 text-center">
                Your details go directly to the Delhigence team. No newsletter, no marketing list.
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function FormField({ label, value, onChange, testid, type = 'text', required = false }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">
        {label}
        {required && <span className="text-emerald-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        required={required}
        className="w-full bg-slate-950/50 border border-white/10 focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40 outline-none rounded-lg px-4 py-3 text-white placeholder-slate-600 transition"
      />
    </div>
  );
}

/* ─── Inline demo-data mocks (no real client numbers) ────────────── */

// Format INR compactly: 5000000 → "5,00,00,000"
function inr(n) {
  return n.toLocaleString('en-IN');
}

function WizardGauntletMock() {
  const steps = [
    'Description specificity',
    'Justification quality per item',
    'Vendor quotation authenticity',
    'Letterhead-request completeness',
    'Committee resolution presence',
  ];
  return (
    <div className="relative rounded-xl border border-emerald-400/20 bg-gradient-to-br from-emerald-950/30 to-slate-900/80 p-8">
      <div className="text-xs uppercase tracking-widest text-emerald-400/80 mb-6">The 5-step AI gauntlet</div>
      <div className="space-y-3">
        {steps.map((step, si) => (
          <div key={si} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/5">
            <div className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-400/40 grid place-items-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 text-emerald-300" strokeWidth={3} />
            </div>
            <div className="text-sm text-slate-200 font-medium">{step}</div>
            <div className="ml-auto text-[10px] uppercase tracking-wider text-emerald-400/70">Step {si + 1}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-5 border-t border-white/5 text-xs text-slate-400 leading-relaxed">
        Only claims that clear all five reach the reviewer. Coaching suggestions surface inline — the AI tells the secretary <em className="text-white">how</em> to fix a weak justification, not just <em className="text-white">that</em> it&rsquo;s weak.
      </div>
    </div>
  );
}

function BudgetMock() {
  // Demo numbers only. Plausible mid-size sports body figures.
  const budgeted = 50000000;   // ₹5.00 Cr
  const consumed = 18520000;   // ₹1.85 Cr
  const remaining = budgeted - consumed;
  const atRisk = 2;
  const totalHeads = 24;

  const heads = [
    { name: 'Broadcast & Production', budget: 12000000, used: 10800000 },
    { name: 'Ground rentals & pitch prep', budget: 8500000, used: 3200000 },
    { name: 'Umpires & match officials', budget: 4200000, used: 1650000 },
    { name: 'Hospitality & travel', budget: 6800000, used: 6720000 },
    { name: 'Cheerleaders & entertainment', budget: 3500000, used: 400000 },
    { name: 'Sponsorship activation', budget: 5200000, used: 750000 },
  ];

  return (
    <div className="relative rounded-xl border border-white/10 bg-slate-900/80 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-slate-950/60">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
        <div className="ml-3 text-[11px] text-slate-500 font-mono">console.mpl / dashboard · demo data</div>
      </div>

      <div className="grid grid-cols-4 gap-px bg-white/5">
        {[
          { l: 'Total Budgeted', v: `₹${inr(budgeted)}`, sub: `${totalHeads} heads`, tone: 'text-white' },
          { l: 'Consumed', v: `₹${inr(consumed)}`, sub: `${Math.round((consumed / budgeted) * 100)}% of total`, tone: 'text-amber-300' },
          { l: 'Remaining', v: `₹${inr(remaining)}`, sub: `${Math.round((remaining / budgeted) * 100)}% left`, tone: 'text-emerald-300' },
          { l: 'At Risk / Exceeded', v: `${atRisk} / ${totalHeads}`, sub: 'heads flagged', tone: 'text-rose-300' },
        ].map((k, i) => (
          <div key={i} className="bg-slate-900 px-4 py-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">{k.l}</div>
            <div className={`text-lg font-bold tabular-nums ${k.tone}`}>{k.v}</div>
            <div className="text-[10px] text-slate-500 mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="px-5 pt-5 pb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs font-semibold text-white tracking-wide">Budget-head consumption · Open PO Tracker</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">6 of {totalHeads} heads shown</div>
        </div>
        <div className="space-y-3.5">
          {heads.map((h, i) => {
            const pct = (h.used / h.budget) * 100;
            const overrun = pct >= 98;
            const risk = pct >= 85 && !overrun;
            const barColor = overrun
              ? 'from-rose-500 to-rose-400'
              : risk
              ? 'from-amber-500 to-amber-400'
              : 'from-emerald-500 to-emerald-400';
            const labelColor = overrun ? 'text-rose-300' : risk ? 'text-amber-300' : 'text-emerald-300';
            return (
              <div key={i}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <div className="text-sm text-slate-200 font-medium truncate pr-3">{h.name}</div>
                  <div className="text-xs tabular-nums text-slate-400 flex-shrink-0">
                    <span className={`font-semibold ${labelColor}`}>₹{inr(h.used)}</span>
                    <span className="text-slate-600"> / ₹{inr(h.budget)}</span>
                  </div>
                </div>
                <div className="relative h-2 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className={`absolute left-0 top-0 bottom-0 rounded-full bg-gradient-to-r ${barColor}`}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <div className={`text-[10px] font-semibold uppercase tracking-wider ${labelColor}`}>
                    {overrun ? 'Near overrun' : risk ? 'At risk' : 'On track'}
                  </div>
                  <div className="text-[10px] text-slate-500 tabular-nums">{pct.toFixed(1)}% consumed</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EmailHubMock() {
  const issues = [
    { pri: 'HIGH', tag: 'follow-up', title: 'Share GST certificate for broadcast policy', from: 'insurance vendor', ago: '2 h ago' },
    { pri: 'HIGH', tag: 'payment', title: 'Process payment for manpower services (50% milestone)', from: 'ops vendor', ago: '5 h ago' },
    { pri: 'MED', tag: 'approval needed', title: 'Release management fee PO — pending sign-off', from: 'internal', ago: '1 d ago' },
    { pri: 'MED', tag: 'follow-up', title: 'Confirm receipt of test transaction', from: 'accounts vendor', ago: '2 d ago' },
  ];
  const priTone = {
    HIGH: 'bg-rose-500/15 text-rose-300 border-rose-400/30',
    MED: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
    LOW: 'bg-slate-500/15 text-slate-300 border-slate-400/30',
  };
  return (
    <div className="relative rounded-xl border border-white/10 bg-slate-900/80 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-slate-950/60">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
        <div className="ml-3 text-[11px] text-slate-500 font-mono">console.mpl / email-hub · demo data</div>
      </div>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div>
          <div className="text-white font-semibold text-sm">Open Issues</div>
          <div className="text-[11px] text-slate-500 mt-0.5">AI-extracted actionable items · auto-refreshed every 15 min</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-center">
            <div className="text-2xl font-bold text-white tabular-nums">124</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">open</div>
          </div>
          <div className="w-px h-8 bg-white/10" />
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-300 tabular-nums">38</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">resolved this wk</div>
          </div>
        </div>
      </div>
      <div className="divide-y divide-white/5">
        {issues.map((it, i) => (
          <div key={i} className="px-5 py-3.5 hover:bg-white/[0.02] transition">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold border ${priTone[it.pri]}`}>
                {it.pri}
              </span>
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-400/20">
                OPEN
              </span>
              <span className="text-[10px] text-slate-400 bg-white/5 px-1.5 py-0.5 rounded">{it.tag}</span>
              <span className="ml-auto text-[10px] text-slate-500">{it.ago}</span>
            </div>
            <div className="text-sm text-slate-100 font-medium leading-snug mb-0.5">{it.title}</div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-indigo-400" /> AI-extracted
              </span>
              <span>·</span>
              <span>From: {it.from}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApprovalsMock() {
  const rows = [
    { name: 'Broadcast production — Aug milestone', tour: 'MPL 2026', status: 'Pending', invoices: 2, amount: 1250000 },
    { name: 'Ground preparation — Ground 2', tour: 'MPL 2026', status: 'Approved', invoices: 1, amount: 480000 },
    { name: 'Cheerleader troupe — league finale', tour: 'MPL 2026', status: 'Draft', invoices: 1, amount: 220000 },
    { name: 'Umpire honorarium — matchweek 4', tour: 'MPL 2026', status: 'Approved', invoices: 3, amount: 165000 },
  ];
  const statusTone = {
    Draft: 'bg-slate-500/15 text-slate-300 border-slate-400/30',
    Pending: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
    Approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  };
  return (
    <div className="relative rounded-xl border border-white/10 bg-slate-900/80 overflow-hidden shadow-2xl">
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/5 bg-slate-950/60">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
        <div className="ml-3 text-[11px] text-slate-500 font-mono">console.mpl / approvals · demo data</div>
      </div>
      <div className="grid grid-cols-4 gap-px bg-white/5">
        {[
          { l: 'Total', v: '12', tone: 'text-white' },
          { l: 'Draft', v: '3', tone: 'text-slate-300' },
          { l: 'Pending', v: '5', tone: 'text-amber-300' },
          { l: 'Approved', v: '4', tone: 'text-emerald-300' },
        ].map((k, i) => (
          <div key={i} className="bg-slate-900 px-4 py-3.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{k.l}</div>
            <div className={`text-xl font-bold tabular-nums ${k.tone}`}>{k.v}</div>
          </div>
        ))}
      </div>
      <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
        <div className="text-xs text-slate-400">Total across all approvals</div>
        <div className="text-lg font-bold text-white tabular-nums">₹{inr(2115000)}</div>
      </div>
      <div className="px-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2.5 text-left font-semibold">Approval</th>
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              <th className="px-3 py-2.5 text-right font-semibold">Invoices</th>
              <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-white/[0.02] transition">
                <td className="px-3 py-3">
                  <div className="text-slate-100 font-medium text-[13px] leading-tight">{r.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{r.tour}</div>
                </td>
                <td className="px-3 py-3">
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-1 rounded border font-semibold ${statusTone[r.status]}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-3 text-right text-slate-300 tabular-nums text-[13px]">{r.invoices}</td>
                <td className="px-3 py-3 text-right text-white font-semibold tabular-nums text-[13px]">
                  ₹{inr(r.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-white/5 text-[11px] text-slate-500 flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-indigo-300" />
        AI-drafted approval request emails include the invoice, PO, and budget-head context inline.
      </div>
    </div>
  );
}

